import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import CreateSleepScreenModal from './CreateSleepScreenModal';
import SleepScreenCreatedModal from './SleepScreenCreatedModal';
import SleepScreenCard from './SleepScreenCard';
import { ListSleepScreens } from '../../libs/GraphQL';
import styles from './SleepScreensLanding.module.css';

const compareByCreatedAtDesc = (a, b) => {
  // Newest first; entries without createdAt sink to the bottom.
  if (!a.createdAt && !b.createdAt) return 0;
  if (!a.createdAt) return 1;
  if (!b.createdAt) return -1;
  return b.createdAt.localeCompare(a.createdAt);
};

const SleepScreensLanding = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdEntry, setCreatedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchEntries = async () => {
      setLoading(true);
      try {
        const result = await ListSleepScreens();
        if (cancelled) return;
        setEntries((result || []).slice().sort(compareByCreatedAtDesc));
      } catch (err) {
        if (cancelled) return;
        console.error('listSleepScreens failed:', err);
        toast.error('Failed to load sleep screen library.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEntries();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreated = (entry) => {
    setShowCreateModal(false);
    setCreatedEntry(entry);
    setEntries((prev) => [
      entry,
      ...prev.filter((e) => e.sleepScreenId !== entry.sleepScreenId),
    ]);
  };

  const filteredEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter((entry) => {
      const name = (entry.displayName || '').toLowerCase();
      const id = (entry.sleepScreenId || '').toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  }, [entries, searchTerm]);

  const hasSearch = searchTerm.trim().length > 0;

  const renderListBody = () => {
    if (loading) {
      return <div className={styles.stateBlock}>Loading…</div>;
    }
    if (entries.length === 0) {
      return (
        <div className={styles.stateBlock}>
          No sleep screens yet — click <strong>+ new entry</strong> to add the
          first one to the library.
        </div>
      );
    }
    if (filteredEntries.length === 0) {
      return (
        <div className={styles.stateBlock}>
          No entries match &quot;{searchTerm.trim()}&quot;.
        </div>
      );
    }
    return (
      <div className={styles.grid}>
        {filteredEntries.map((entry) => (
          <SleepScreenCard key={entry.sleepScreenId} entry={entry} />
        ))}
      </div>
    );
  };

  return (
    <main className={styles.container}>
      <div className={styles.headerRow}>
        <h1>Sleep Screen Library</h1>
        <button
          type="button"
          className={styles.createButton}
          onClick={() => setShowCreateModal(true)}
        >
          + new entry
        </button>
      </div>

      <div className={styles.toolbar}>
        <span className={styles.entryCount}>
          {hasSearch
            ? `${filteredEntries.length} of ${entries.length} entries`
            : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
        </span>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="search by name or id…"
          aria-label="Search sleep screens by name or ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {renderListBody()}

      <CreateSleepScreenModal
        show={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onCreated={handleCreated}
      />

      <SleepScreenCreatedModal
        entry={createdEntry}
        onClose={() => setCreatedEntry(null)}
      />
    </main>
  );
};

export default SleepScreensLanding;
